<template>
    <div>
        <!-- Page Hero -->
        <section class="page-hero d-flex align-items-center justify-content-center text-center text-white position-relative"
            style="background-color: #333; height: 400px; overflow: hidden;">
            <img src="https://placehold.co/1920x400/333333/666666?text=Product+Details"
                class="position-absolute w-100 h-100 object-fit-cover opacity-50" alt="Hero">
            <div class="position-relative z-1 mt-5">
                <h1 class="display-4 fw-bold font-serif mb-2">Gorgeous Leather Chair</h1>
                <nav aria-label="breadcrumb">
                    <ol class="breadcrumb justify-content-center mb-0">
                        <li class="breadcrumb-item"><a href="/"
                                class="text-white text-decoration-none small">Home</a></li>
                        <li class="breadcrumb-item"><a href="/products"
                                class="text-white text-decoration-none small">Products</a></li>
                        <li class="breadcrumb-item active text-white-50 small" aria-current="page">Gorgeous Leather Chair
                        </li>
                    </ol>
                </nav>
            </div>
        </section>
        <div class="position-relative">
            <div class="torn-paper-edge"></div>
        </div>

        <!-- Main Content -->
        <section class="py-5 bg-light-creme" v-if="!loading && product">
            <div class="container">
                <div class="row g-5">
                    <!-- Image Gallery -->
                    <div class="col-lg-6">
                        <div class="position-relative mb-3 bg-white p-4 rounded-4 shadow-sm text-center">
                            <span v-if="product.old_price > product.price" class="position-absolute top-0 start-0 m-3 badge bg-danger rounded-pill px-3 py-2">Sale
                                {{ product.discount_percentage }}%</span>
                            <div class="position-absolute top-0 end-0 m-3 d-flex flex-column gap-2">
                                <button class="btn btn-light rounded-circle shadow-sm btn-sm"><i
                                        class="bi bi-arrows-fullscreen"></i></button>
                                <button class="btn btn-light rounded-circle shadow-sm btn-sm" @click="toggleFavorite"><i
                                        class="bi bi-heart" :class="{'text-danger': product.isFavored}"></i></button>
                            </div>
                            <img :src="activeImage || product.default_image.large" class="img-fluid"
                                :alt="product.name" style="max-height: 500px; object-fit: contain;">
                        </div>
                        <div class="d-flex gap-3 justify-content-center overflow-auto">
                            <div class="bg-white p-2 rounded-3 border cursor-pointer" 
                                 :class="{'border-brown': activeImage === img.large}"
                                 v-for="(img, index) in product.images" :key="index"
                                 @click="setActiveImage(img.large)">
                                <img :src="img.thumb" width="60" :alt="'Thumb ' + index">
                            </div>
                        </div>
                    </div>

                    <!-- Product Info -->
                    <div class="col-lg-6">
                        <div class="mb-3">
                            <span class="text-success small fw-bold" v-if="product.stock > 0 || product.can_buy"><i class="bi bi-check-circle me-1"></i> IN STOCK</span>
                            <span class="text-danger small fw-bold" v-else><i class="bi bi-x-circle me-1"></i> OUT OF STOCK</span>
                            <span class="text-warning small ms-3">
                                <i class="bi bi-star-fill"></i> <i class="bi bi-star-fill"></i> <i
                                    class="bi bi-star-fill"></i> <i class="bi bi-star-fill"></i> <i
                                    class="bi bi-star-half"></i>
                                <span class="text-muted text-decoration-underline ms-1">(5 reviews)</span>
                            </span>
                        </div>
                        <h2 class="fw-bold font-serif mb-2">{{ product.name }}</h2>
                        <div class="mb-3">
                            <span class="h3 fw-bold text-brown me-2">EGP {{ product.price }}</span>
                            <span v-if="product.old_price > product.price" class="text-muted text-decoration-line-through">EGP {{ product.old_price }}</span>
                        </div>
                        <!-- <p class="small text-muted mb-4"><i class="bi bi-truck me-2"></i> Deliver in 2 Days</p> -->

                        <p class="text-muted mb-4 small" v-html="product.mini_description || product.description"></p>

                        <!-- Timer (Placeholder or Real if Offer) -->
                        <div class="d-flex align-items-center gap-3 mb-4" v-if="product.availableOffer">
                             <span class="small fw-bold text-muted">Time running out:</span>
                             <!-- Logic for countdown can be added later similar to PromoSection -->
                             <span class="badge bg-danger">Offer Active!</span>
                        </div>

                        <div class="d-flex gap-3 mb-4">
                            <div class="input-group" style="width: 130px;">
                                <button class="btn btn-outline-secondary" type="button" @click="decrementQuantity">-</button>
                                <input type="text" class="form-control text-center border-secondary" v-model="quantity" readonly>
                                <button class="btn btn-outline-secondary" type="button" @click="incrementQuantity">+</button>
                            </div>
                            <button class="btn btn-brown text-white w-100 rounded-1" @click="addToCart">Add To Cart <i
                                    class="bi bi-cart ms-2"></i></button>
                        </div>

                        <div class="small text-muted">
                            <div class="mb-1"><span class="fw-bold text-dark">SKU:</span> {{ product.code || 'N/A' }}</div>
                            <div class="mb-1"><span class="fw-bold text-dark">Categories:</span> 
                                <span v-for="(cat, idx) in product.categories" :key="idx">{{ cat.name }}<span v-if="idx < product.categories.length - 1">, </span></span>
                            </div>
                            <!-- <div class="mb-1"><span class="fw-bold text-dark">Tags:</span> Design</div> -->
                            <!-- <div class="mb-1"><span class="fw-bold text-dark">Brand:</span> Mosea</div> -->
                        </div>
                    </div>
                </div>

                <!-- Tabs -->
                <div class="mt-5">
                    <ul class="nav nav-pills mb-4 justify-content-start gap-3" id="pills-tab" role="tablist">
                        <li class="nav-item" role="presentation">
                            <button class="btn btn-brown text-white rounded-pill px-4 active" id="pills-desc-tab"
                                data-bs-toggle="pill" data-bs-target="#pills-desc" type="button">Description</button>
                        </li>
                        <li class="nav-item" role="presentation">
                            <button class="btn btn-outline-secondary rounded-pill px-4" id="pills-reviews-tab"
                                data-bs-toggle="pill" data-bs-target="#pills-reviews" type="button">Reviews (5)</button>
                        </li>
                    </ul>
                    <div class="tab-content text-muted small" id="pills-tabContent">
                        <div class="tab-pane fade show active" id="pills-desc" role="tabpanel" v-html="product.description || 'No description available.'">
                        </div>
                        <div class="tab-pane fade" id="pills-reviews" role="tabpanel">
                            Reviews content here...
                        </div>
                    </div>
                </div>

                <!-- Related Products -->
                <div class="mt-5" v-if="relatedProducts.length > 0">
                    <div class="d-flex justify-content-between align-items-center mb-4">
                        <h3 class="fw-bold font-serif">Related Products</h3>
                        <div class="d-flex gap-2">
                            <!-- Navigation buttons if carousel implemented later -->
                        </div>
                    </div>
                    <div class="row g-4">
                        <div class="col-md-3" v-for="related in relatedProducts" :key="related.id">
                            <div class="card border-0 h-100 product-card bg-white p-3 rounded-4 shadow-sm">
                                <span v-if="related.old_price > related.price" class="badge bg-danger position-absolute top-0 start-0 m-3 rounded-pill">Sale
                                    {{ related.discount_percentage }}%</span>
                                <a :href="'/product/' + related.id">
                                    <img :src="related.default_image.medium"
                                        class="card-img-top product-img mx-auto d-block" :alt="related.name">
                                </a>
                                <div class="card-body px-0 pb-0 text-start">
                                    <a :href="'/product/' + related.id" class="text-decoration-none text-dark">
                                        <h6 class="fw-bold small text-truncate">{{ related.name }}</h6>
                                    </a>
                                    <div class="text-warning small mb-2"><i class="bi bi-star-fill"></i><i
                                            class="bi bi-star-fill"></i><i class="bi bi-star-fill"></i><i
                                            class="bi bi-star-fill"></i><span class="text-muted ms-1">(4.0)</span></div>
                                    <div class="fw-bold text-brown">EGP {{ related.price }} <span v-if="related.old_price > related.price"
                                            class="text-muted text-decoration-line-through small fw-normal">EGP {{ related.old_price }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </section>
        <div v-else class="py-5 text-center">
            <div class="spinner-border text-brown" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>
    </div>
</template>

<script>
import Navbar from './Navbar.vue';

export default {
    components: { Navbar },
    props: ['pId'],
    data() {
        return {
            product: null,
            relatedProducts: [],
            loading: true,
            activeImage: null,
            quantity: 1
        }
    },
    mounted() {
        this.fetchProduct();
        this.fetchRelatedProducts();
    },
    methods: {
        async fetchProduct() {
            try {
                this.loading = true;
                const response = await axios.get(`/api/frontend/products/${this.pId}`);
                this.product = response.data.data;
                this.activeImage = this.product.default_image.large;
                this.loading = false;
            } catch (error) {
                console.error('Error fetching product:', error);
                this.loading = false;
            }
        },
        async fetchRelatedProducts() {
            try {
                const response = await axios.get(`/api/frontend/products/related/${this.pId}`);
                this.relatedProducts = response.data.data;
            } catch (error) {
                console.error('Error fetching related products:', error);
            }
        },
        setActiveImage(image) {
            this.activeImage = image;
        },
        addToCart() {
             // Check auth implicitly via API or explicitly if authUser prop available (it is not currently passed in props but we can check window or props)
             // Ideally AuthUser should be passed or stored globally. 
             // ProductDetails doesn't have authUser prop yet. Let's assume passed or check API response.
             // Actually, I can check if global authUser exists or just try request.
             
            axios.post('/api/frontend/cart/add', {
                product_id: this.product.id,
                quantity: this.quantity,
                price: this.product.price
            })
            .then(response => {
                const cart = response.data.data;
                let count = 0;
                if(cart && cart.cart_items) {
                     count = cart.cart_items.length;
                }
                window.Bus.$emit('cart-updated', count);
                
                this.$swal.fire({
                    icon: 'success',
                    title: 'Added to Cart!',
                    text: 'Product has been added to your cart.',
                    timer: 2000,
                    showConfirmButton: false,
                    toast: true,
                    position: 'top-end'
                });
            })
            .catch(error => {
                console.error('Error adding to cart:', error);
                 if (error.response && error.response.status === 401) {
                     window.location.href = '/customer/login';
                }
            });
        },
        incrementQuantity() {
            this.quantity++;
        },
        decrementQuantity() {
            if (this.quantity > 1) this.quantity--;
        },
        toggleFavorite() {
            axios.post(`/api/frontend/toggleFavored/${this.product.id}`)
                .then(response => {
                    this.product.isFavored = !this.product.isFavored;
                    window.Bus.$emit('wishlist-updated');
                    
                    if (this.product.isFavored) {
                        this.$swal.fire({
                            icon: 'success',
                            title: 'Added to Wishlist!',
                            text: 'Product has been added to your favorites.',
                            timer: 2000,
                            showConfirmButton: false,
                            toast: true,
                            position: 'top-end'
                        });
                    } else {
                        this.$swal.fire({
                            icon: 'info',
                            title: 'Removed from Wishlist',
                            text: 'Product has been removed from your favorites.',
                            timer: 2000,
                            showConfirmButton: false,
                            toast: true,
                            position: 'top-end'
                        });
                    }
                })
                .catch(error => {
                     if (error.response && error.response.status === 401) {
                         window.location.href = '/customer/login';
                    }
                    console.error('Error toggling favorite:', error);
                });
        }
    }
}
</script>
